import React, { useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShieldAlt,
  faUsers,
  faFilePdf,
  faInfoCircle,
  faCalendarAlt,
  faUser,
  faVenusMars,
  faEnvelope,
  faHome,
} from "@fortawesome/free-solid-svg-icons";
import "./ScoreResultModal.css";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { sendAipReport } from "../utils/api";
import { formatYyyyDdMm } from "../utils/date";

const ScoreResultModal = ({ isOpen, onClose, scores, clientData }) => {
  const reportRef = useRef(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendStatus, setSendStatus] = useState(null);
  const [emailSent, setEmailSent] = useState(false);

  const normalizedScores = {
    aipScore: Number(scores?.aipScore ?? scores?.aip) || 0,
    wellBeingScore: Number(scores?.wellBeingScore ?? scores?.wellBeing) || 0,
    careTeamScore: Number(scores?.careTeamScore ?? scores?.careTeam) || 0,
    environmentScore:
      Number(scores?.environmentScore ?? scores?.environment) || 0,
  };

  // Determine status color/text based on AIP Score
  let statusColor = "#52c41a"; // Green
  let statusText = "Good";
  let trendText = "Supporting Aging in Place";

  if (normalizedScores.aipScore < 50) {
    statusColor = "#f5222d"; // Red
    statusText = "Critical";
    trendText = "Requires Immediate Attention";
  } else if (normalizedScores.aipScore < 75) {
    statusColor = "#faad14"; // Orange background stays
    statusText = "Moderate";
    trendText = "Needs Improvement";
  }

  // Early exit after hooks are defined
  if (!isOpen) return null;

  const generatePdfDataUrl = async () => {
    if (!reportRef.current) throw new Error("Report not mounted");

    // Ensure images are loaded to avoid UNKNOWN type errors
    const imgs = Array.from(
      reportRef.current.querySelectorAll("img")
    );
    await Promise.all(
      imgs.map(
        (img) =>
          new Promise((resolve) => {
            if (img.complete) return resolve();
            img.onload = () => resolve();
            img.onerror = () => resolve();
          })
      )
    );

    // Capture with robust html2canvas settings
    const canvas = await html2canvas(reportRef.current, {
      useCORS: true,
      allowTaint: true,
      scale: 2,
      logging: false,
      imageTimeout: 0,
      backgroundColor: "#ffffff",
    });
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "pt", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgProps = pdf.getImageProperties(imgData);
    const pdfWidth = pageWidth;
    const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

    let position = 0;
    let remainingHeight = pdfHeight;

    while (remainingHeight > 0) {
      pdf.addImage(
        imgData,
        "PNG",
        0,
        position,
        pdfWidth,
        pdfHeight,
        undefined,
        "FAST"
      );
      remainingHeight -= pageHeight;
      if (remainingHeight > 0) {
        position = position - pageHeight;
        pdf.addPage();
      }
    }

    return pdf.output("datauristring");
  };

  const handleDownloadPdf = async () => {
    try {
      const dataUrl = await generatePdfDataUrl();
      const pdf = new jsPDF("p", "pt", "a4");
      const img = dataUrl;
      const pageWidth = pdf.internal.pageSize.getWidth();
      const imgProps = pdf.getImageProperties(img);
      const pdfWidth = pageWidth;
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

      let position = 0;
      let remainingHeight = pdfHeight;

      while (remainingHeight > 0) {
        pdf.addImage(
          img,
          "PNG",
          0,
          position,
          pdfWidth,
          pdfHeight,
          undefined,
          "FAST"
        );
        remainingHeight -= pdf.internal.pageSize.getHeight();
        if (remainingHeight > 0) {
          position = position - pdf.internal.pageSize.getHeight();
          pdf.addPage();
        }
      }

      const clientIdForFile = typeof clientData?.clientId === 'string' || typeof clientData?.clientId === 'number'
        ? clientData.clientId
        : (clientData?.clientNumber || "client");
      pdf.save(`aip-report-${clientIdForFile}.pdf`);
    } catch (err) {
      console.error("PDF download failed:", err);
      alert(
        "Failed to generate PDF. Please try again or contact support if the issue persists."
      );
    }
  };

  const handleSendEmail = async () => {
    if (!recipientEmail) {
      alert("Please enter recipient email");
      return;
    }
    try {
      setSendingEmail(true);
      setSendStatus(null);
      const dataUrl = await generatePdfDataUrl();
      const pdfBase64 = dataUrl.split(",")[1];
      const clientIdForEmail = typeof clientData?.clientId === 'string' || typeof clientData?.clientId === 'number'
        ? clientData.clientId
        : (clientData?.clientNumber || clientData?._id || null);
      
      const payload = {
        recipientEmail,
        clientId: clientIdForEmail,
        reportData: { 
          scores, 
          clientData: {
            clientId: clientIdForEmail,
            clientNumber: clientData?.clientNumber,
            firstName: clientData?.firstName,
            lastName: clientData?.lastName,
            gender: clientData?.gender,
            dob: clientData?.dob,
            primaryCondition: clientData?.primaryCondition
          }
        },
        pdfBase64,
      };
      await sendAipReport(payload);
      setSendStatus({ success: true, message: "Report sent successfully" });
      setEmailSent(true);
      setShowEmailModal(false);
      setTimeout(() => setEmailSent(false), 3000);
    } catch (err) {
      console.error("Send email failed:", err);
      setSendStatus({
        success: false,
        message:
          err?.response?.data?.message ||
          "Failed to send email. Please try again.",
      });
    } finally {
      setSendingEmail(false);
    }
  };

  // Helper for current date
  const today = formatYyyyDdMm(new Date());

  // Simple AI-like narrative generator using scores
  const buildSummary = () => {
    const wb = normalizedScores.wellBeingScore || 0;
    const ct = normalizedScores.careTeamScore || 0;
    const aip = normalizedScores.aipScore || 0;

    const wbLevel = wb >= 75 ? "strong" : wb >= 40 ? "moderate" : "low";
    const ctLevel =
      ct >= 75 ? "well-coordinated" : ct >= 40 ? "developing" : "fragmented";
    const aipLevel = aip >= 75 ? "high" : aip >= 40 ? "moderate" : "low";

    const overall = `Overall finding: Aging-in-place readiness is ${aipLevel}, supported by ${wbLevel} personal health indicators and ${ctLevel} care team delivery.`;
    const clinical = `Clinical implications: Focus on stabilizing health and strengthening care coordination to reduce risk. Leverage existing positives while addressing gaps in adherence, monitoring, and timely visits.`;
    const focus = `Recommended focus: Prioritize ${
      wbLevel === "low"
        ? "foundational health management (med adherence, vitals, lifestyle)"
        : "optimizing lifestyle and functional reserve"
    } and ${
      ctLevel === "fragmented"
        ? "building reliable, on-time care delivery"
        : "maintaining reliable scheduling and communication"
    } to raise overall AIP capacity.`;

    return { overall, clinical, focus };
  };

  const { overall, clinical, focus } = buildSummary();

  return (
    <div className="score-modal-overlay">
      <div className="score-modal-container" ref={reportRef}>
        {/* Header Section */}
        <div className="score-report-header">
          <h2>Aging in Place Comprehensive Assessment Report</h2>

          <div className="report-actions-top">
              {emailSent && (
                <div
                  style={{
                    background: "#ecfdf3",
                    color: "#166534",
                    border: "1px solid #bbf7d0",
                    borderRadius: "8px",
                    padding: "8px 12px",
                    fontWeight: 600,
                  }}
                >
                  ✓ Email sent successfully!
                </div>
              )}
            <button className="download-btn pdf" onClick={handleDownloadPdf}>
              <FontAwesomeIcon icon={faFilePdf} /> DOWNLOAD AS PDF
            </button>
            <button
              className="download-btn email"
              onClick={() => setShowEmailModal(true)}
            >
              <FontAwesomeIcon icon={faEnvelope} /> SEND AS EMAIL
            </button>
          </div>

          <div className="report-meta-tags">
            <span className="meta-tag">
              <FontAwesomeIcon icon={faUser} /> Client #{" "}
              {clientData?.clientNumber || clientData?.clientId || "N/A"}
            </span>
            <span className="meta-tag">
              <FontAwesomeIcon icon={faVenusMars} /> Gender:{" "}
              {clientData?.gender || "N/A"}
            </span>
            <span className="meta-tag">
              <FontAwesomeIcon icon={faCalendarAlt} /> DOB:{" "}
              {clientData?.dob || "N/A"}
            </span>
            <span className="meta-tag blue">
              <FontAwesomeIcon icon={faCalendarAlt} /> Generated: {today}
            </span>
          </div>
          <button className="close-btn" onClick={onClose}>
            &times;
          </button>
        </div>

        {/* Top Content: Gauge + Summary */}
        <div className="report-content-top">
          {/* Left: Score Gauge */}
          <div className="score-gauge-section">
            <div className="circular-chart">
              <svg viewBox="0 0 36 36" className="circular-chart-svg">
                <path
                  className="circle-bg"
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                />
                <path
                  className="circle"
                  strokeDasharray={`${normalizedScores.aipScore}, 100`}
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  style={{ stroke: statusColor }}
                />
                <text x="18" y="20.35" className="percentage">
                  {normalizedScores.aipScore}
                </text>
                <text x="18" y="27" className="percentage-label">
                  AIP Score
                </text>
              </svg>
            </div>
            <div
              className="status-pill"
              style={{ backgroundColor: statusColor }}
            >
              {statusText}
            </div>
            <div className="trend-text" style={{ color: statusColor }}>
              {trendText}
            </div>
            <div className="status-label">AIP Score</div>
            <div
              className="status-score"
              style={{ color: statusColor }}
            >
              {normalizedScores.aipScore ?? "N/A"}
            </div>
          </div>

          {/* Right: Text Summary */}
          <div className="summary-text-section">
            <h4 className="summary-title">Aging in Place Assessment Summary</h4>

            <div className="summary-block">
              <strong>Overall Finding:</strong> {overall}
            </div>

            <div className="summary-block">
              <strong>Clinical Implications:</strong> {clinical}
            </div>

            <div className="summary-block">
              <strong>Recommended Focus:</strong> {focus}
            </div>
          </div>
        </div>

        {/* Independence Indicator Bar */}
        <div className="independence-bar">
          <strong>Independence Indicator:</strong> Current indicators strongly
          support the client's ability to age safely and independently in their
          current environment.
        </div>

        {/* Component Analysis Cards */}
        <h4 className="component-title">AIP Score Component Analysis</h4>
        <div className="component-cards-row">
          <div className="comp-card orange-border">
            <div className="comp-icon orange">
              <FontAwesomeIcon icon={faShieldAlt} />
            </div>
            <div className="comp-score orange">
              {normalizedScores.wellBeingScore}
            </div>
            <div className="comp-name">Well-Being Score</div>
            <div className="comp-desc">
              Health, function, and lifestyle factors
            </div>
          </div>

          <div className="comp-card green-border">
            <div className="comp-icon green">
              <FontAwesomeIcon icon={faUsers} />
            </div>
            <div className="comp-score green">{normalizedScores.careTeamScore}</div>
            <div className="comp-name">Care Team Score</div>
            <div className="comp-desc">Service delivery and coordination</div>
          </div>

          <div className="comp-card blue-border">
            <div className="comp-icon blue">
              <FontAwesomeIcon icon={faHome} />
            </div>
            <div className="comp-score blue">
              {normalizedScores.environmentScore}
            </div>
            <div className="comp-name">Environment Score</div>
            <div className="comp-desc">Home safety and environmental risk</div>
          </div>
        </div>

        {/* Disclaimer Footer */}
        <div className="disclaimer-box">
          <div className="disclaimer-icon">
            <FontAwesomeIcon icon={faInfoCircle} />
          </div>
          <div className="disclaimer-text">
            <strong>Important Disclaimer</strong>
            <br />
            This Aging in Place (AIP) assessment integrates health and care team
            factors to evaluate independence capacity. This tool provides
            decision support information and should not replace professional
            clinical judgment. All care decisions should involve qualified
            healthcare providers and consider individual client circumstances
            and preferences.
          </div>
        </div>
      </div>

      {showEmailModal && (
        <div
          className="email-modal-overlay"
          onClick={() => setShowEmailModal(false)}
        >
          <div
            className="email-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Send Report</h3>
            <label>Email address</label>
            <input
              type="email"
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              placeholder="recipient@example.com"
            />
            <div className="email-modal-actions">
              <button onClick={() => setShowEmailModal(false)}>Cancel</button>
              <button onClick={handleSendEmail} disabled={sendingEmail}>
                {sendingEmail ? "Sending..." : "Send"}
              </button>
            </div>
            {sendStatus && (
              <div
                style={{
                  marginTop: "8px",
                  color: sendStatus.success ? "#16a34a" : "#dc2626",
                }}
              >
                {sendStatus.message}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ScoreResultModal;
